import React, { useLayoutEffect, useRef, useState } from "react";
import { Area, Bar as ChartBar, BarChart, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import ShotZoneChart from "../PlayerProfile/sections/ShotZoneChart";
import PercentileRadar, { RADAR_EMPTY, RADAR_GROUPS_LABEL, RADAR_TITLE, radarCaption } from "./PercentileRadar";
import BrandLogo, { BRAND_DOMAINS } from "../../common/BrandLogo";
import HudlIcon from "../../common/HudlIcon";
import { getPlatformKey } from "../../../utils/utils";
import { normalizeUrl, platformOf, teamKindLabel } from "./edit/editHubModel";
import { CLOUDINARY_CLOUD_NAME } from "../../../constants";
import { fmtIndex10 } from "../../../utils/indexScore";
import { benchmarkScale, benchmarkSummary, edgeAnchor } from "../../../utils/benchmarkScale";
import { displayTeamName } from "../../../utils/teamNames";
import { Badge, BmModal, Card, NoticeState, Overline, SegmentedControl } from "../../ForBrands/ui/BrandUI";
import { SegmentedControl as PhoneSegmentedControl } from "../../auth/v2/pieces";
import { EMPTY_VALUE, fmtCompact, fmtMoney } from "./athleteUtils";
import { ENGAGEMENT_MARKS, engagementText } from "./profileNarratives";
import insightGlyph from "../../../assets/brand/profile/insight-glyph-16.svg";
import infoIcon from "../../../assets/brand/profile/info-18.svg";
import instagramTile from "../../../assets/brand/edit-hub/menu-instagram.svg";
import tiktokTile from "../../../assets/brand/edit-hub/menu-tiktok.svg";
import xTile from "../../../assets/brand/edit-hub/menu-x.svg";
import youtubeTile from "../../../assets/brand/edit-hub/menu-youtube.svg";

// Athlete Mode · My Profile V2 building blocks (Figma 02 · My Profile - V2).
// Every block renders from the shared profile model (profileModel.js), so
// the numbers here are the same ones the public, coach and brand views show
// (business/athlette/profile). Each section mirrors the feature set of the
// matching PlayerProfile section in the V2 design system.

const CHIP_TONE = {
  blue: { tone: "outline", dot: "#2f6fed" },
  gray: { tone: "passive" },
  green: { tone: "outline", dot: "#1f9d55" },
  yellow: { tone: "outline", dot: "#edd200" },
  orange: { tone: "brand" },
};

export const ModelChip = ({ chip }) => {
  const meta = CHIP_TONE[chip.variant] || CHIP_TONE.gray;
  return (
    <Badge tone={meta.tone} dot={meta.dot}>
      {chip.label}
    </Badge>
  );
};

export const InfoDot = ({ onClick, label = "More information" }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    className="flex h-5 w-5 items-center justify-center rounded-full border border-bm-strong bm-label-xs text-bm-muted hover:border-bm-ink hover:text-bm-ink"
  >
    i
  </button>
);

export const SectionTitle = ({ title, subtitle, right }) => (
  <div className="flex items-start justify-between gap-4">
    <div className="flex flex-col gap-[6px]">
      <h2 className="bm-h-m text-bm-ink">{title}</h2>
      {subtitle && <span className="bm-body-m text-bm-muted">{subtitle}</span>}
    </div>
    {right}
  </div>
);

// Figma "InsightBox" (profile variant): 16px glyph and an overline head,
// with an optional subtitle under the head, in line with the label.
export const ProfileInsight = ({ label, subtitle, children, className = "" }) => {
  const head = (
    <div className="flex items-center gap-[10px]">
      <img src={insightGlyph} alt="" className="h-4 w-4" />
      <span className="bm-overline">{label}</span>
    </div>
  );
  return (
    <div className={`flex flex-col gap-3 rounded-[8px] border border-bm-border bg-white px-5 py-[18px] ${className}`}>
      {subtitle ? (
        <div className="flex flex-col gap-1">
          {head}
          <p className="pl-[26px] bm-body-s text-bm-muted">{subtitle}</p>
        </div>
      ) : (
        head
      )}
      <p className="bm-body-m text-bm-muted">{children}</p>
    </div>
  );
};

// Chart legend entry: 22px outline badge with a 7px dot, or a ring for a
// hollow mark (`hollow`, the season chart's camp games).
export const LegendBadge = ({ color, label, hollow = false }) => (
  <span className="inline-flex h-[22px] items-center gap-[6px] rounded-full border border-bm-strong bg-white px-[9px] bm-label-xs text-bm-ink">
    <span className="h-[7px] w-[7px] rounded-full" style={hollow ? { border: `2px solid ${color}` } : { background: color }} />
    {label}
  </span>
);

// A percentile rank on a stat: the filled blue badge, or passive. A rank
// that names its group (the model's `scope`, "Top 3% in Nevada 5A 2026")
// wraps inside a narrow tile instead of running out of it; a plain "Top
// N%" keeps its one-line 22px badge.
export const RankBadge = ({ chip, filled = false }) => (
  <span
    className={`inline-flex ${chip.scope ? "min-h-[22px] max-w-full py-1 text-center" : "h-[22px]"} items-center rounded-full px-[9px] bm-label-xs ${filled ? "bg-[#2196f3] text-white" : "bg-bm-border text-bm-muted"}`}
  >
    {chip.label}
  </span>
);

// View tabs on the Current season card (Figma "div.vtab").
export const ViewTab = ({ active, children, onClick }) => (
  <button type="button" onClick={onClick} className={`rounded-full px-[22px] py-[11px] text-[14px] font-semibold leading-none ${active ? "bg-bm-ink text-white" : "border border-bm-strong bg-white text-bm-muted hover:text-bm-ink"}`}>{children}</button>
);

// Metric with a badge under the number (Figma "MetricCard" on the season card).
export const StatMetric = ({ label, value, chip }) => (
  <div className="flex min-w-0 flex-1 flex-col items-start gap-2 rounded-[12px] border border-bm-border bg-white px-6 py-5">
    <Overline>{label}</Overline>
    <span className="bm-num-l text-bm-ink">{value}</span>
    {chip && <ModelChip chip={chip} />}
  </div>
);

export const ProfileCardShell = ({ children, className = "", padding = "p-6" }) => (
  <Card padding={padding} className={`flex flex-col gap-5 ${className}`}>
    {children}
  </Card>
);

// ------------------------------------------------------------------ Hero
// The V2 platform tiles (edit-hub menu logos: a 20px rounded square in the
// platform colour with a white mark). Facebook and Snapchat have no V2 asset,
// so they are drawn inline in the same tile style; Hudl keeps its own mark.
const TILE = 20;
const AssetTile = (src) => {
  const Tile = () => <img src={src} alt="" width={TILE} height={TILE} className="block h-5 w-5" />;
  return Tile;
};

const FacebookTile = () => (
  <svg width={TILE} height={TILE} viewBox="0 0 20 20" aria-hidden="true" focusable="false" className="block">
    <rect width="20" height="20" rx="4" fill="#1877F2" />
    <path d="M11 16.5v-5h1.7l.3-2H11V8.3c0-.6.2-1 1-1h1.1V5.6c-.2 0-.9-.1-1.6-.1-1.6 0-2.5 1-2.5 2.7v1.3H7.2v2H9v5h2z" fill="#fff" />
  </svg>
);

const SnapchatTile = () => (
  <svg width={TILE} height={TILE} viewBox="0 0 20 20" aria-hidden="true" focusable="false" className="block">
    <rect width="20" height="20" rx="4" fill="#FFFC00" />
    <path
      d="M10 4.5c1.9 0 3.3 1.4 3.3 3.3v1.3l1.1-.3c.3-.1.6.2.4.5-.3.4-.9.6-1.4.8.4 1.1 1.3 1.9 2.4 2.2.3.1.3.5 0 .6-.5.2-1.1.3-1.6.4-.1.4-.2.8-.6.8-.6 0-1.1-.3-1.9 0-.6.3-1 .9-1.7.9s-1.1-.6-1.7-.9c-.8-.3-1.3 0-1.9 0-.4 0-.5-.4-.6-.8-.5-.1-1.1-.2-1.6-.4-.3-.1-.3-.5 0-.6 1.1-.3 2-1.1 2.4-2.2-.5-.2-1.1-.4-1.4-.8-.2-.3.1-.6.4-.5l1.1.3V7.8c0-1.9 1.4-3.3 3.3-3.3z"
      fill="#fff"
      stroke="#2c2c2c"
      strokeWidth="0.8"
      strokeLinejoin="round"
    />
  </svg>
);

const HudlTile = () => <HudlIcon size={TILE - 2} />;

// 247Sports and On3 have no V2 asset: a 20px ink tile with the short name.
const TextTile = (label) => {
  const Tile = () => (
    <span aria-hidden="true" className="flex h-5 w-5 items-center justify-center rounded-[4px] bg-bm-ink text-[7px] font-bold leading-none text-white">
      {label}
    </span>
  );
  return Tile;
};

// ESPN has no V2 asset either: its name in white on ESPN red, drawn inline in
// the same tile style as Facebook and Snapchat.
const EspnTile = () => (
  <svg width={TILE} height={TILE} viewBox="0 0 20 20" aria-hidden="true" focusable="false" className="block">
    <rect width="20" height="20" rx="4" fill="#CC0000" />
    <text x="10" y="12.4" textAnchor="middle" fontFamily="Arial, Helvetica, sans-serif" fontSize="6.4" fontWeight="800" fill="#fff">
      ESPN
    </text>
  </svg>
);

// The player profile sites (recruiting and stats pages; an athlete can have
// several): each its own link, shown after the (at most five) social and
// highlight links. A saved link belongs to the one its host names.
const RECRUITING_SITES = [
  ["247sports", "247Sports"],
  ["on3", "On3"],
  ["espn", "ESPN"],
];
export const RECRUITING_KEYS = new Set(RECRUITING_SITES.map(([key]) => key));
export const recruitingLinksOf = (socialMedia) => {
  const links = (Array.isArray(socialMedia) ? socialMedia : []).filter((u) => typeof u === "string" && u.trim());
  return RECRUITING_SITES.map(([key, name]) => {
    const url = links.find((u) => linkKeyOf(u) === key);
    return url ? { key, name, url: normalizeUrl(url) } : null;
  }).filter(Boolean);
};
// The platform a saved link belongs to, for the links the profile draws with
// their own tile: a known network (getPlatformKey; a link saved without
// https:// still counts) or a recruiting site. null for anything else.
export const linkKeyOf = (url) => {
  const key = getPlatformKey(normalizeUrl(url));
  if (key) return key;
  const recruiting = platformOf(url);
  return RECRUITING_KEYS.has(recruiting) ? recruiting : null;
};

const SOCIAL_PLATFORMS = {
  instagram: { name: "Instagram", Tile: AssetTile(instagramTile) },
  x: { name: "X", Tile: AssetTile(xTile) },
  twitter: { name: "X", Tile: AssetTile(xTile) },
  tiktok: { name: "TikTok", Tile: AssetTile(tiktokTile) },
  youtube: { name: "YouTube", Tile: AssetTile(youtubeTile) },
  facebook: { name: "Facebook", Tile: FacebookTile },
  snapchat: { name: "Snapchat", Tile: SnapchatTile },
  hudl: { name: "Hudl", Tile: HudlTile },
  "247sports": { name: "247Sports", Tile: TextTile("247") },
  on3: { name: "On3", Tile: TextTile("On3") },
  espn: { name: "ESPN", Tile: EspnTile },
};

// One round button per linked social (Hudl opens the highlight reel), named
// for its platform, then the profile sites (247Sports, On3, ESPN), each its
// own link.
export const SocialLinks = ({ links }) => {
  const seen = new Set();
  const all = (links || [])
    .map((url) => {
      const key = linkKeyOf(url);
      const platform = SOCIAL_PLATFORMS[key];
      if (!platform || seen.has(platform.name)) return null;
      seen.add(platform.name);
      // A link saved without https:// would otherwise open as a page of ours.
      return { url: normalizeUrl(url), key, ...platform };
    })
    .filter(Boolean);
  const items = [...all.filter((i) => !RECRUITING_KEYS.has(i.key)).slice(0, 5), ...all.filter((i) => RECRUITING_KEYS.has(i.key))];
  if (!items.length) return null;
  return (
    <span className="flex items-center gap-2">
      {items.map(({ url, key, name, Tile }) => (
        <a
          key={key}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={name}
          title={name}
          className="flex h-10 w-10 items-center justify-center rounded-full border border-bm-border bg-white text-bm-ink hover:bg-bm-subtle"
        >
          <Tile />
        </a>
      ))}
    </span>
  );
};

export const MetaChips = ({ chips }) =>
  chips?.length ? (
    <div className="flex flex-wrap gap-2">
      {chips.map((c) => (
        <Badge key={c} tone="outline" size="m">
          {c}
        </Badge>
      ))}
    </div>
  ) : null;

// Every team on the profile (allTeams from athlete/by-email/), the main
// team first. Shown beside the College, High school and Club chips, since
// an athlete can play for a school team and a club team (and more) at once.
// Each says what kind of team it is (the server's `kind`: College, High
// school, Club, Pro), whatever field it was filed in.
export const orderedTeams = (teams) => {
  const list = (Array.isArray(teams) ? teams : []).filter((t) => t && String(t.teamName || t.displayName || "").trim());
  return [...list.filter((t) => t.isPrimary), ...list.filter((t) => !t.isPrimary)];
};

// A team's title by the one display rule (utils/teamNames, D15): the
// server's displayName, else its name with the age group its league key
// names ("Oakland Soldiers 16U"), never a season CACHE added.
export const teamTitleOf = (t) => {
  if (!t || typeof t !== "object") return "";
  const name = String(t.teamName || t.name || "").trim();
  return String(displayTeamName({ ...t, teamName: name, name }) || name).trim();
};

export const teamChipText = (t) =>
  [teamTitleOf(t), teamKindLabel(t.kind, t.junior === true), t.isPrimary ? "Primary" : t.verified ? "" : "Unverified"]
    .filter(Boolean)
    .join(" · ");

export const TeamChips = ({ teams }) => {
  const list = orderedTeams(teams);
  if (!list.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-2" aria-label="Teams">
      <Overline>Teams</Overline>
      {list.map((t, i) => (
        <Badge key={`${t.league || ""}|${t.teamId || t.teamName}|${i}`} tone={t.isPrimary ? "brand" : "outline"} size="m">
          {teamChipText(t)}
        </Badge>
      ))}
    </div>
  );
};

// Fixed, face-aware avatar crop (Cloudinary g_auto:face); object-top is
// the CSS fallback for other hosts.
export const faceCrop = (url) =>
  url && url.includes("/upload/") ? url.replace("/upload/", "/upload/w_640,h_800,c_fill,g_auto:face,f_auto,q_auto/") : url;
export const DEFAULT_AVATAR = `https://res.cloudinary.com/${CLOUDINARY_CLOUD_NAME}/image/upload/default-avatar.png`;

// ------------------------------------------------------------ CacheScore
export const RingScore = ({ value, confidence }) => {
  const size = 200;
  const r = 86;
  const c = 2 * Math.PI * r;
  const pct = value == null ? 0 : Math.max(0, Math.min(100, value)) / 100;
  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="absolute inset-0 -rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} stroke="#f2f2f4" strokeWidth="12" fill="none" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke="#FF4400"
          strokeWidth="12"
          fill="none"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - pct)}
          style={{ transition: "stroke-dashoffset 900ms ease" }}
        />
      </svg>
      <div className="flex w-[120px] flex-col items-center gap-[2px] text-center">
        <span className="bm-label-xs text-bm-muted">CacheScore™</span>
        <span className="font-archivo text-[56px] font-bold leading-[60px] text-bm-ink">{value == null ? EMPTY_VALUE : Math.round(value)}</span>
        {confidence && (
          <span className="flex flex-col items-center leading-tight">
            <span className="bm-body-s text-bm-muted">Confidence</span>
            <span className="bm-label-l text-bm-ink">{confidence}</span>
          </span>
        )}
      </div>
    </div>
  );
};

// A pillar's bar (Performance, Visibility, Off-court impact): the pillar
// out of 10 beside its label ("6.1/10", utils/indexScore) and the bar filled
// to its 0 to 100 score; the en dash for a pillar never computed.
export const Bar = ({ label, value }) => (
  <div className="flex flex-col gap-2">
    <div className="flex items-center justify-between">
      <span className="bm-label-l text-bm-ink">{label}</span>
      <span className="bm-label-l text-bm-ink">{fmtIndex10(value, { suffix: true })}</span>
    </div>
    <div className="h-[6px] w-full overflow-hidden rounded-full bg-bm-mutedbg">
      <div className="h-full rounded-full bg-bm-orange" style={{ width: `${Math.max(0, Math.min(100, value || 0))}%`, transition: "width 900ms ease" }} />
    </div>
  </div>
);

// ------------------------------------------------------------- Valuation
export const RangeMeter = ({ range, badge, badgeTone = "dark", label = "Suggested Market Rate" }) => {
  if (!range) {
    return <p className="py-6 text-center bm-body-m text-bm-muted">Not available yet</p>;
  }
  const span = Math.max(1, range.ceiling - range.floor);
  const pos = Math.max(0, Math.min(100, ((range.value - range.floor) / span) * 100));
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-end justify-between gap-4">
        <span className="bm-body-s text-bm-muted">{label}</span>
        <span className="flex flex-col items-end gap-1">
          {badge && <Badge tone={badgeTone}>{badge}</Badge>}
          <span className="bm-num-l text-bm-ink">{fmtMoney(range.value)}</span>
        </span>
      </div>
      <div className="relative h-[6px] rounded-full bg-bm-mutedbg">
        <div className="absolute left-0 top-0 h-full rounded-full bg-bm-orange" style={{ width: `${pos}%` }} />
        <span
          className="absolute top-1/2 h-[14px] w-[14px] -translate-x-1/2 -translate-y-1/2 rounded-full border-[3px] border-white bg-bm-orange shadow"
          style={{ left: `${pos}%` }}
        />
      </div>
      <div className="flex justify-between bm-body-s text-bm-muted">
        <span>
          {fmtMoney(range.floor)}
          <br />
          Floor
        </span>
        <span className="text-center text-bm-orange">{fmtMoney(range.value)}</span>
        <span className="text-right">
          {fmtMoney(range.ceiling)}
          <br />
          Ceiling
        </span>
      </div>
    </div>
  );
};

// ------------------------------------------------------------ Stat tiles
// Figma "StatCell": 16px corners, value over an overline, percentile ranks
// below: a state rank in blue, a peer group rank passive (the legend's
// colours, so a lone peer group rank never reads as a state one).
export const StatTile = ({ value, label, chips = [], sub }) => (
  <div className="flex min-w-0 flex-col items-center gap-2 rounded-[16px] border border-bm-border bg-white px-2 py-4 text-center">
    <span className="bm-num-ml text-bm-ink">{value == null ? "–" : value}</span>
    <span className="bm-overline">{label}</span>
    {sub && <span className="bm-body-s text-bm-muted">{sub}</span>}
    {chips.length > 0 && (
      <span className="flex flex-col items-center gap-[6px]">
        {chips.map((c, i) => (
          <RankBadge key={`${c.variant}-${c.label}-${i}`} chip={c} filled={c.variant === "blue"} />
        ))}
      </span>
    )}
  </div>
);

// Where the box score's stats come from (D18), under its title: the notice
// while they are not live ("Stats as of Oct 12, 2026. Live updates are
// paused."), then the "Stats from" line naming each counted team with its
// games. Nothing when the model has neither. `compact` is the phone card's
// smaller type.
export const StatsProvenance = ({ model, compact = false }) => {
  if (!model?.statsStale && !model?.statsSources) return null;
  return (
    <div className="flex flex-col gap-2">
      {model.statsStale && <NoticeState role="status">{model.statsStale}</NoticeState>}
      {model.statsSources && <p className={`${compact ? "bm-body-xs" : "bm-body-s"} break-words text-bm-muted`}>{model.statsSources}</p>}
    </div>
  );
};

// The box score's play group switch (Kobi's 10/21 note, D7): School |
// Grassroots, with College and Pro when the athlete played there, under
// the card's header on a card whose model has two or more playGroups;
// nothing with fewer (the subtitle names the one group). `groups` are the
// model's playGroups, `value` the shown group's key. `compact` is the phone
// look (a radio group across the card, equal segments as the radar's), else
// the desktop's segmented buttons (aria-pressed). Past three groups the
// phone segments take their words' width (a 320px card gives four equal
// segments about 60px, less than "Grassroots" needs).
export const PLAY_GROUP_SWITCH_LABEL = "Box score for";
export const PlayGroupSwitch = ({ groups = [], value, onChange, compact = false }) => {
  if (!Array.isArray(groups) || groups.length < 2) return null;
  return compact ? (
    <PhoneSegmentedControl
      label={PLAY_GROUP_SWITCH_LABEL}
      value={value}
      onChange={onChange}
      options={groups.map((g) => [g.key, g.label])}
      segmentClassName={groups.length > 3 ? "flex-auto whitespace-nowrap px-1" : "flex-1"}
    />
  ) : (
    <div role="group" aria-label={PLAY_GROUP_SWITCH_LABEL} className="flex">
      <SegmentedControl value={value} onChange={onChange} capitalize={false} options={groups.map((g) => ({ value: g.key, label: g.label }))} />
    </div>
  );
};

// Figma "div.box": number and name on the left, definition, rank pills on
// the right (blue for a state rank, as on the tiles). Pills that name their
// group keep to a column of their own width and wrap there.
export const ShootingRow = ({ item }) => {
  const chips = item.chips || [];
  const named = chips.some((c) => c.scope);
  return (
    <div className="flex items-center gap-5 rounded-[16px] border border-bm-border bg-white p-[25px]">
      <span className="flex w-[120px] shrink-0 flex-col gap-3">
        <span className="bm-num-l text-bm-ink">{item.value ?? "–"}</span>
        <span className="bm-label-l text-bm-ink">{item.label}</span>
      </span>
      <span className="min-w-0 flex-1 bm-body-m text-bm-muted">{item.desc}</span>
      {chips.length > 0 && (
        <span className={`flex shrink-0 flex-col items-start gap-3${named ? " max-w-[176px]" : ""}`}>
          {chips.map((c, i) => (
            <span
              key={`${c.variant}-${c.label}-${i}`}
              className={`inline-flex ${c.scope ? "min-h-[18px] max-w-full py-[3px] leading-[12px]" : "h-[18px] leading-none"} items-center rounded-full px-3 text-[10px] font-bold text-white ${c.variant === "blue" ? "bg-[#2196f3]" : "bg-bm-muted"}`}
            >
              {c.label}
            </span>
          ))}
        </span>
      )}
    </div>
  );
};

export const ScoringBreakdown = ({ breakdown }) =>
  breakdown ? (
    <div className="flex flex-col gap-2">
      <span className="bm-label-l text-bm-ink">Scoring breakdown</span>
      <div className="flex h-[10px] w-full overflow-hidden rounded-full bg-bm-mutedbg">
        {breakdown.map((b) => (
          <span key={b.label} style={{ width: `${b.share}%`, background: b.color }} title={`${b.label} ${b.share}%`} />
        ))}
      </div>
      <div className="flex flex-wrap gap-4 bm-body-s text-bm-muted">
        {breakdown.map((b) => (
          <span key={b.label} className="flex items-center gap-2">
            <span className="h-[7px] w-[7px] rounded-full" style={{ background: b.color }} />
            {b.label} · {b.share}% ({Math.round(b.points * 10) / 10} pts)
          </span>
        ))}
      </div>
    </div>
  ) : null;

// The chart carries its own V2 heading, legends and court. Not drawn on
// any profile: its dots are generated, not real shot data (ShotZoneChart),
// so the player radar stands where it was at every level. Kept for when
// shot locations exist.
export const ShotZone = ({ profile }) => <ShotZoneChart profile={profile} />;

// Player radar (Figma note on 1504:5385): a group's percentiles as one
// shape, headed bm-h-s over a bm-body-s caption naming the group, or why
// there is none yet. `groups` is the profile model's `radarGroups`
// (League, College, Pro); with two or more the small V2 segmented control
// picks one, League first.
export const PlayerRadar = ({ groups = [] }) => {
  const [picked, setPicked] = useState(null);
  const group = groups.find((g) => g.key === picked) || groups[0] || null;
  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-3">
        <h3 className="bm-h-s text-bm-ink">{RADAR_TITLE}</h3>
        {group && <span className="bm-body-s text-bm-muted">{radarCaption(group.peerLabel)}</span>}
      </div>
      {groups.length > 1 && (
        <div role="group" aria-label={RADAR_GROUPS_LABEL} className="flex">
          <SegmentedControl value={group.key} onChange={setPicked} options={groups.map((g) => ({ value: g.key, label: g.tab }))} />
        </div>
      )}
      {group ? <PercentileRadar axes={group.radar} peerLabel={group.peerLabel} /> : <p className="bm-body-s text-bm-muted">{RADAR_EMPTY}</p>}
    </div>
  );
};

export const LegendDot = ({ color, label }) => (
  <span className="flex items-center gap-2 bm-body-s text-bm-muted">
    <span className="h-[7px] w-[7px] rounded-full" style={{ background: color }} /> {label}
  </span>
);

// ---------------------------------------------------------- Season charts
// A season chart point's number: one decimal, the en dash without one.
const pointNumber = (v) => (typeof v === "number" && Number.isFinite(v) ? `${Math.round(v * 10) / 10}` : EMPTY_VALUE);

// A season chart point in words, for screen readers: "Jul 17, win 72-65 vs.
// Team United, Nike EYBL - Peach Jam in Augusta, SC: JJ 24 points, league
// average 10.9 PPG". `point` is a model point (profileModel's seasonPoint)
// with the group average (`avg`) when the chart draws one; `name` is whose
// number it is ("JJ", "You"), `unit` "points" for a game and "PPG" for an
// event's average. The phone bars and both popovers say it the same way.
export const seasonPointText = (point, { name, avgName = "Average", unit = "PPG" }) => {
  const average = avgName.toLowerCase().replace(/\bavg\b/, "average");
  return `${point.label}${point.detail ? `, ${point.detail}` : ""}: ${name} ${pointNumber(point.me)} ${unit}${
    point.avg != null ? `, ${average} ${pointNumber(point.avg)} PPG` : ""
  }`;
};

const PointValue = ({ mark, label, value }) => (
  <span className="flex items-center gap-2">
    <span className={`h-[7px] w-[7px] shrink-0 rounded-full ${mark}`} />
    <span className="min-w-0 flex-1 break-words text-bm-muted">{label}</span>
    <span className="shrink-0 whitespace-nowrap text-bm-ink">{value}</span>
  </span>
);

// One game or event of the season chart (Kobi's 10/2 note: the opponent and
// the place on the timeline's popovers). A game reads its date and event
// ("Jul 17 · Nike EYBL - Peach Jam"), the result, score and opponent ("W
// 72-65 vs. Team United") and the place ("Augusta, SC"); an event its name,
// team and games ("Team Why Not · 4 games") and the place. Then the
// athlete's number and the group average when the chart draws one, and a
// Camp or All-star game tag for a game that is on the timeline but not in
// the season's numbers. A line the payload has no data for is left out.
// The desktop chart's tooltip and the phone bars' panel both draw it:
// `compact` is the phone panel (the subtle fill, phone type, the bars'
// marks). Screen readers get seasonPointText, the way the bars say it.
export const SeasonPointCard = ({ point, name = "Athlete", avgName = "Average", unit = "PPG", compact = false }) => {
  const meta = compact ? "bm-body-xs" : "bm-body-s";
  const mine = point.camp ? (compact ? "bg-bm-orange/40" : "border-2 border-bm-orange bg-white") : "bg-bm-orange";
  return (
    <div
      className={
        compact
          ? "flex flex-col rounded-[12px] bg-bm-subtle p-3"
          : "flex w-[240px] flex-col rounded-[12px] border border-bm-border bg-white p-3 shadow-[0_8px_24px_rgba(0,0,0,0.14)]"
      }
    >
      <div aria-hidden="true" className="flex flex-col gap-1">
        <span className="flex items-start justify-between gap-2">
          <span className="min-w-0 break-words bm-label-l text-bm-ink">{point.title || point.label}</span>
          {point.campLabel && <Badge tone="passive">{point.campLabel}</Badge>}
        </span>
        {point.matchup && <span className={`${compact ? "bm-body-s" : "bm-body-m"} break-words text-bm-ink`}>{point.matchup}</span>}
        {point.sub && <span className={`${meta} break-words text-bm-muted`}>{point.sub}</span>}
        {point.location && <span className={`${meta} break-words text-bm-muted`}>{point.location}</span>}
        <span className={`flex flex-col gap-[2px] pt-1 ${meta}`}>
          <PointValue mark={mine} label={name} value={`${pointNumber(point.me)} ${unit}`} />
          {point.avg != null && (
            <PointValue mark={compact ? "bg-bm-strong" : "bg-bm-tertiary"} label={avgName} value={`${pointNumber(point.avg)} PPG`} />
          )}
        </span>
      </div>
      <span className="sr-only">{seasonPointText(point, { name, avgName, unit })}</span>
    </div>
  );
};

// The season chart's popover: recharts hands it the point under the pointer,
// or under the arrow keys once the chart has focus (its keyboard layer). The
// box stays mounted and speaks the point politely as it changes.
export const SeasonChartTip = ({ active, payload, name, avgName, unit }) => {
  const point = active && Array.isArray(payload) && payload.length ? payload[0]?.payload : null;
  return (
    <div role="status" aria-live="polite">
      {point && <SeasonPointCard point={point} name={name} avgName={avgName} unit={unit} />}
    </div>
  );
};

// The athlete's dot on the season chart (recharts hands it the point): a
// filled orange dot, hollow for a camp or all-star game, a size up when
// it is the active point.
export const SeasonDot = ({ cx, cy, payload, active = false }) => {
  if (!Number.isFinite(cx) || !Number.isFinite(cy)) return null;
  return payload?.camp ? (
    <circle cx={cx} cy={cy} r={active ? 7 : 6} fill="#fff" stroke="#FF4400" strokeWidth={3} />
  ) : (
    <circle cx={cx} cy={cy} r={active ? 8 : 7} fill="#FF4400" stroke="#fff" strokeWidth={active ? 2 : 0} />
  );
};

// One point per game (that game's points) or per event (its average), with
// the peer group's average as a dashed line when the comparison covers the
// season; `avgName` names it ("League avg", "Position avg"). Past twelve
// points the axis keeps its ends and drops the labels that would collide.
// A point's popover (SeasonChartTip) names the game or event; camp games
// draw hollow. `name` is whose points they are and `unit` the number's
// ("points" per game, "PPG" per event). The chart takes keyboard focus and
// the arrow keys walk its points.
export const SeasonChart = ({ series, avgName = "Average", name = "Athlete", unit = "PPG" }) => (
  <div className="h-[334px]">
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={series} margin={{ top: 10, right: 10, left: -10, bottom: 0 }} accessibilityLayer aria-label="Points per game">
        <defs>
          <linearGradient id="v2PpgFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#FF4400" stopOpacity={0.18} />
            <stop offset="100%" stopColor="#FF4400" stopOpacity={0.02} />
          </linearGradient>
        </defs>
        <CartesianGrid vertical={false} stroke="#f2f2f4" />
        <XAxis
          dataKey="label"
          tickLine={false}
          axisLine={false}
          tick={{ fill: "#757575", fontSize: 13 }}
          interval={series.length > 12 ? "preserveStartEnd" : 0}
        />
        <YAxis tickLine={false} axisLine={false} tick={{ fill: "#757575", fontSize: 13 }} />
        <Tooltip cursor={{ stroke: "#d9d9d9" }} content={<SeasonChartTip name={name} avgName={avgName} unit={unit} />} />
        <Area type="linear" dataKey="me" stroke="none" fill="url(#v2PpgFill)" activeDot={false} />
        {series.some((s) => s.avg != null) && (
          <Line type="linear" dataKey="avg" stroke="#b8b8bc" strokeWidth={2} strokeDasharray="5 5" dot={false} name={avgName} />
        )}
        <Line type="linear" dataKey="me" stroke="#FF4400" strokeWidth={3} dot={<SeasonDot />} activeDot={<SeasonDot active />} name="PPG" />
      </ComposedChart>
    </ResponsiveContainer>
  </div>
);

// The athlete against an average, one bar pair per row ({ stat, you, avg }).
// `avgName` names the average's bars and `label` the athlete's in the
// tooltip: "You" on their own page (the default), their first name for a
// viewer.
export const ComparisonChart = ({ rows, avgName = "Average", label = "You" }) => (
  <div className="h-[240px]">
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={rows} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
        <CartesianGrid vertical={false} stroke="#f2f2f4" />
        <XAxis dataKey="stat" tickLine={false} axisLine={false} tick={{ fill: "#b8b8bc", fontSize: 11 }} />
        <YAxis tickLine={false} axisLine={false} tick={{ fill: "#b8b8bc", fontSize: 11 }} />
        <Tooltip />
        <ChartBar dataKey="avg" fill="#d9d9d9" name={avgName} radius={[4, 4, 0, 0]} />
        <ChartBar dataKey="you" fill="#FF4400" name={label} radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  </div>
);

// vs. teammates (the model's `currentSeason.teammates`): the team, its
// competition and size, the athlete against the team average for PPG, APG
// and RPG, and the athlete's rank on the team in words, so the numbers read
// without the chart. `compact` is the phone card's smaller type.
export const TeamComparison = ({ teammates, label = "You", compact = false }) => (
  <div className="flex flex-col gap-3">
    {teammates.caption && <p className={`${compact ? "bm-body-xs" : "bm-body-s"} text-bm-muted`}>{teammates.caption}</p>}
    <ComparisonChart rows={teammates.rows} avgName="Team avg" label={label} />
    {/* eslint-disable-next-line jsx-a11y/no-redundant-roles -- VoiceOver drops the list role of a list with no bullets */}
    <ul role="list" aria-label="Team ranks" className="flex flex-col gap-1">
      {teammates.rows.map((r) => (
        <li key={r.stat} className={compact ? "bm-body-s" : "bm-body-m"}>
          <span className="text-bm-ink">{r.rank || r.stat}</span>
          <span className="text-bm-muted">{` · ${r.youText} vs. ${r.avgText} team avg`}</span>
        </li>
      ))}
    </ul>
  </div>
);

// ------------------------------------------------------------ Visibility
// Figma "MetricCard": linked platforms in a bordered 12px tile, unlinked in the muted one.
export const PlatformTile = ({ platform, onAdd }) => {
  const body = (
    <>
      <Overline>{platform.name}</Overline>
      <span className={`bm-num-l ${platform.linked ? "text-bm-ink" : "text-bm-ink"}`}>{platform.linked ? fmtCompact(platform.followers) : EMPTY_VALUE}</span>
      {platform.linked ? (
        <span className="flex flex-wrap items-center gap-2">
          <span className="bm-body-s text-bm-muted">{platform.engagement != null ? `${platform.engagement.toFixed(1)}% engagement` : "Linked"}</span>
          {platform.verified && <Badge tone="outline" dot="#1f9d55">Verified</Badge>}
        </span>
      ) : (
        <span className="bm-body-s text-bm-muted">Not linked</span>
      )}
    </>
  );
  if (!platform.linked && onAdd) {
    return (
      <button type="button" onClick={onAdd} title={`Add your ${platform.name} to boost your score`} className="flex min-w-0 flex-1 flex-col items-start gap-2 rounded-[12px] bg-bm-mutedbg px-6 py-5 text-left hover:bg-bm-border">
        {body}
      </button>
    );
  }
  return <div className={`flex min-w-0 flex-1 flex-col gap-2 rounded-[12px] px-6 py-5 ${platform.linked ? "border border-bm-border bg-white" : "bg-bm-mutedbg"}`}>{body}</div>;
};

// A label on a bar mark, lined up as benchmarkScale's anchor says: centred
// on the mark, or starting (ending) at it near the bar's start (end), so it
// never runs off the bar.
export const anchoredStyle = (pos, anchor) =>
  anchor === "end" ? { right: `${100 - pos}%` } : anchor === "start" ? { left: `${pos}%` } : { left: `${pos}%`, transform: "translateX(-50%)" };
const ANCHOR_ALIGN = { start: "items-start text-left", center: "items-center text-center", end: "items-end text-right" };

// The engagement bar's geometry and accessible name from the profile's
// marks (profileNarratives.ENGAGEMENT_MARKS): one scale for the dot, the
// fill, the ticks and every label (utils/benchmarkScale, D5).
export const engagementBar = (engagement) => {
  const scale = benchmarkScale({ value: engagement, low: ENGAGEMENT_MARKS.low, high: ENGAGEMENT_MARKS.high });
  // The words read the rate as its label prints it (one decimal, as the
  // visibility read rounds it too): 1.79% is "1.8%, at the position
  // average", never "1.8%, below the position average of 1.8%".
  const shown = typeof engagement === "number" && Number.isFinite(engagement) ? Number(engagement.toFixed(1)) : null;
  const label = benchmarkSummary({
    value: shown,
    low: ENGAGEMENT_MARKS.low,
    high: ENGAGEMENT_MARKS.high,
    name: "Engagement rate",
    lowName: ENGAGEMENT_MARKS.lowName,
    highName: ENGAGEMENT_MARKS.highName,
    format: (v) => engagementText(v, { mark: true }),
    formatValue: (v) => engagementText(v),
  });
  return { scale, label };
};

// The bar's break on an axis an outlier stretched past three times the top
// mark (benchmarkScale's broken axis): a white notch with ruled sides.
const BarBreak = ({ pos, className = "h-[12px]" }) => (
  <span
    aria-hidden="true"
    className={`absolute top-1/2 w-[6px] -translate-x-1/2 -translate-y-1/2 border-x-2 border-bm-strong bg-white ${className}`}
    style={{ left: `${pos}%` }}
  />
);

// A mark's tick across the bar.
const BarTick = ({ pos, className = "h-[14px]" }) => (
  <span aria-hidden="true" className={`absolute top-1/2 w-[2px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-bm-muted ${className}`} style={{ left: `${pos}%` }} />
);

// Figma "Engagement rate vs. position benchmarks". selfView (the athlete's
// own page, the default) speaks to the athlete; a viewer reads it in the
// third person. The bar (D5): the athlete's rate over the track at its
// true place, the Avg and Top 10% marks as ticks where they really sit and
// their labels under the track growing away from each other; past three
// times the top mark the axis breaks and the rate prints at the end.
export const EngagementBenchmark = ({ engagement, selfView = true }) => {
  const rate = typeof engagement === "number" && Number.isFinite(engagement) ? engagement : null;
  const { scale, label } = engagementBar(rate);
  // The rate is per follower on some platforms and per view on others
  // (social_score: Instagram per follower; TikTok, X and YouTube per view).
  const infoTitle = selfView
    ? "Interactions per follower or per view across your linked platforms, compared with athletes in your position and level."
    : "Interactions per follower or per view across this athlete's linked platforms, compared with athletes in the same position and level.";
  return (
    <div className="flex flex-col gap-4 rounded-[12px] border border-bm-border bg-white p-7">
      <div className="flex items-center justify-between gap-4">
        <span className="bm-h-m text-bm-ink">Engagement rate vs. position benchmarks</span>
        <img src={infoIcon} alt="" title={infoTitle} className="h-[18px] w-[18px] shrink-0 opacity-70" />
      </div>
      <div className="flex flex-col items-end gap-[21px]">
        <div className="flex items-end justify-end gap-[9px]">
          <span className="text-[12px] leading-none text-bm-muted">Current engagement rate</span>
          <span className="text-[32px] font-bold leading-none text-bm-ink">{engagementText(rate)}</span>
        </div>
        <div role="img" aria-label={label} className="w-full">
          {/* The athlete's rate on its own row over the dot. */}
          <div className="relative h-[18px]">
            {scale.valuePos != null && (
              <span
                className="absolute top-0 whitespace-nowrap text-[18px] font-bold leading-none text-bm-orange"
                style={anchoredStyle(scale.valuePos, scale.valueAnchor)}
              >
                {engagementText(rate)}
              </span>
            )}
          </div>
          <div className="relative mt-3 h-[6px] rounded-full bg-bm-border">
            {scale.valuePos != null && <div className="absolute left-0 top-0 h-full rounded-full bg-bm-orange" style={{ width: `${scale.valuePos}%` }} />}
            {scale.breakPos != null && <BarBreak pos={scale.breakPos} />}
            {scale.lowPos != null && <BarTick pos={scale.lowPos} />}
            {scale.highPos != null && <BarTick pos={scale.highPos} />}
            {scale.valuePos != null && (
              <span
                className="absolute top-1/2 h-[18px] w-[18px] -translate-x-1/2 -translate-y-1/2 rounded-full border-[3px] border-bm-orange bg-white"
                style={{ left: `${scale.valuePos}%` }}
              />
            )}
          </div>
          {/* The marks' labels: the average's ends at its tick (from the
              bar's start when it is wider), the top mark's starts at its tick. */}
          <div className="relative mt-4 h-[46px] text-bm-muted">
            {scale.lowPos != null && (
              <span className="absolute left-0 top-0 flex min-w-max flex-col items-end gap-[7px] text-right" style={{ width: `${scale.lowPos}%` }}>
                <span className="text-[18px] font-bold leading-none">{engagementText(ENGAGEMENT_MARKS.low, { mark: true })}</span>
                <span className="text-[12px] leading-none">{ENGAGEMENT_MARKS.lowLabel}</span>
              </span>
            )}
            {scale.highPos != null && (
              <span className="absolute top-0 flex flex-col items-start gap-[7px] whitespace-nowrap text-left" style={{ left: `${scale.highPos}%` }}>
                <span className="text-[18px] font-bold leading-none">{engagementText(ENGAGEMENT_MARKS.high, { mark: true })}</span>
                <span className="text-[12px] leading-none">{ENGAGEMENT_MARKS.highLabel}</span>
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// --------------------------------------------------------- Brand alignment

// Fit chip from the LLM synergy score (same thresholds as the coach/brand view).
export const fitOf = (score) =>
  score >= 75 ? { fit: "Strong fit", variant: "green" } : score >= 55 ? { fit: "Good fit", variant: "blue" } : { fit: "Moderate fit", variant: "yellow" };

// Conflicts only: advisory notes in older cached matches are not clashes.
export const realFlags = (flags) =>
  (flags || []).filter((f) => {
    if (!f) return false;
    if (/\bno\b[^a-z]*(known\s+|hard\s+)?(conflict|risk|flag)/i.test(f)) return false;
    return /conflict|exclusiv|compet|clash|contradict|violat|ineligib/i.test(f);
  });

// Figma "BrandFitCard": category, 44px logo tile, brand in orange, fit dot.
// bm-positive for a strong fit, bm-attention otherwise.
const FIT_DOT = { green: "#1f9d55", blue: "#edd200", yellow: "#edd200" };

export const BrandFitCard = ({ category, brand, domain, mono, fit, variant, topLeft, topRight, note }) => (
  <div className="relative flex flex-col items-center gap-[9px] rounded-[8px] border border-bm-border bg-white px-[14px] py-4 text-center">
    {topLeft && <span className="absolute left-2 top-2">{topLeft}</span>}
    {topRight && <span className="absolute right-2 top-2">{topRight}</span>}
    <span className="bm-h-s text-bm-ink">{category}</span>
    <span className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-[8px] bg-bm-mutedbg bm-label-l text-bm-ink">
      <BrandLogo domain={domain} mono={mono} size={28} />
    </span>
    <span className="bm-h-xs text-bm-orange">{brand}</span>
    <span className="flex h-5 items-center gap-2">
      <span className="h-[10px] w-[10px] rounded-full" style={{ background: FIT_DOT[variant] || FIT_DOT.yellow }} />
      <span className="bm-label-l text-bm-ink">{fit}</span>
    </span>
    {note && <span className="bm-body-s text-bm-attention">{note}</span>}
  </div>
);

// Pool categories are descriptive ("Athletic footwear & apparel"); the tile
// shows the short label the frame uses.
const SHORT_CATEGORY = [
  [/footwear|apparel|sneaker/i, "Apparel"],
  [/drink|nutrition|food|restaurant|delivery/i, "Nutrition"],
  [/gaming|esport/i, "Gaming"],
  [/audio|music/i, "Audio"],
  [/insurance|financ|fintech|bank/i, "Finance"],
  [/telecom|tech|wearable|software/i, "Technology"],
  [/retail|merch/i, "Retail"],
  [/eyewear|gear|equipment/i, "Sports gear"],
  [/recovery|fitness|health/i, "Fitness"],
  [/personal care|beauty|grooming/i, "Personal care"],
  [/mobility|travel|auto/i, "Mobility"],
  [/lifestyle|media|entertainment/i, "Lifestyle"],
];
export const shortCategory = (category) => {
  const c = (category || "").trim();
  if (!c) return "Sponsor";
  const hit = SHORT_CATEGORY.find(([re]) => re.test(c));
  return hit ? hit[1] : c.split(/[/&,·]/)[0].trim();
};

// The Partner badge explains the existing relationship: to the athlete on
// their own page (selfView, the default), about the athlete for a viewer.
const partnerTitle = (basis, selfView) => {
  const whose = selfView ? "your" : "this athlete's";
  return basis === "personal_deal"
    ? `Existing relationship: appears in ${whose} NIL deals`
    : `Existing relationship via ${whose} stated team or league affiliation`;
};

export const MatchTile = ({ match, selfView = true }) => {
  const { fit, variant } = fitOf(match.score || 0);
  const flags = realFlags(match.flags);
  const incumbent = match.incumbencyBasis && match.incumbencyBasis !== "none";
  return (
    <BrandFitCard
      category={shortCategory(match.category)}
      brand={match.brand}
      domain={match.domain}
      mono={(match.brand || "?").slice(0, 2).toUpperCase()}
      fit={fit}
      variant={variant}
      topLeft={incumbent ? <span title={partnerTitle(match.incumbencyBasis, selfView)}><Badge tone="outline" dot="#1f9d55">Partner</Badge></span> : null}
      topRight={flags.length > 0 ? <span title={flags.join("; ")}><Badge tone="outline" dot="#e2241a">Conflict</Badge></span> : null}
      note={match.capReason ? "Ranked behind a same-category match" : null}
    />
  );
};

export const CategoryTile = ({ category }) => (
  <BrandFitCard category={category.name} brand={category.tier} domain={BRAND_DOMAINS[category.mono]?.domain} mono={category.mono} fit={category.fit} variant={category.variant} />
);

// ---------------------------------------------------- Value in context
// Phone anchor row (Figma 2276:8797): the floor and ceiling at the ends and
// the median at its mark, each sub wrapping under its number (w-min keeps a
// label as narrow as its number). The athlete's own amount and name sit on
// their own row over the bar (D5), so they never share this row. A phone
// card is too narrow for the desktop's percentage spacing rules alone, so
// after layout, and again on resize or once the fonts load, a median that
// would touch the floor or the ceiling is hidden.

// The phone card's anchors and upside boxes use short amounts ($155K), as
// the frame does; the sentences keep the full figure.
const fmtMoneyShort = (n) => (n === null || n === undefined || Number.isNaN(Number(n)) ? EMPTY_VALUE : `$${fmtCompact(n)}`);

// The median's words: "National median" over the distribution's group when
// the backend names it ("College women's basketball", on a line of its own
// under the words; screen readers and the tick's tooltip hear "National
// median · College women's basketball"), "Est. median" without the
// distribution.
const medianBasisOf = (c) => (c.medianIsReal ? String(c.basis || "").trim() : "");
const medianTitleOf = (c) => (c.medianIsReal ? ["National median", medianBasisOf(c)].filter(Boolean).join(" · ") : "Estimated median");
const MedianLabel = ({ c, className }) => {
  const basis = medianBasisOf(c);
  return (
    <span className={className}>
      {c.medianIsReal ? "National median" : "Est. median"}
      {basis && (
        <>
          <span className="sr-only"> · </span>
          <span className="block">{basis}</span>
        </>
      )}
    </span>
  );
};

// The CacheValue badge on the context card: the rank with the group beside
// it when the badge does not say "nationally" (a distribution that pooled
// men's and women's valuations: "Top 10%" and "College basketball"), else
// the one badge.
const ContextPercentile = ({ c }) =>
  c.percentileBasis ? (
    <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
      <Badge tone="dark">{c.percentileRank || c.percentile}</Badge>
      <span className="bm-body-s text-bm-muted">{c.percentileBasis}</span>
    </span>
  ) : (
    <Badge tone="dark">{c.percentile}</Badge>
  );

// Labels closer than this (px) count as touching.
const ANCHOR_GAP = 6;
const anchorBox = (ref) => {
  const rect = ref.current && typeof ref.current.getBoundingClientRect === "function" ? ref.current.getBoundingClientRect() : null;
  return rect && rect.width > 0 ? rect : null;
};
const anchorsTouch = (a, b) => Boolean(a && b && a.left < b.right + ANCHOR_GAP && b.left < a.right + ANCHOR_GAP);

const CompactAnchors = ({ c, medianPos, showMedian }) => {
  const rowRef = useRef(null);
  const floorRef = useRef(null);
  const ceilingRef = useRef(null);
  const medianRef = useRef(null);
  const [clash, setClash] = useState(false);

  useLayoutEffect(() => {
    let live = true;
    const measure = () => {
      if (!live) return;
      const median = anchorBox(medianRef);
      const next = anchorsTouch(median, anchorBox(floorRef)) || anchorsTouch(median, anchorBox(ceilingRef));
      setClash((prev) => (prev === next ? prev : next));
    };
    measure();
    window.addEventListener("resize", measure);
    const observer = typeof ResizeObserver === "function" ? new ResizeObserver(measure) : null;
    if (observer && rowRef.current) observer.observe(rowRef.current);
    if (typeof document !== "undefined" && document.fonts?.ready) document.fonts.ready.then(measure).catch(() => {});
    return () => {
      live = false;
      window.removeEventListener("resize", measure);
      if (observer) observer.disconnect();
    };
  }, [c.floor, c.ceiling, c.median, c.basis, showMedian]);

  // A hidden label keeps its box (visibility), so it can be measured again.
  // A median that names its group takes a fixed width the group wraps in,
  // and the row grows to the extra lines.
  const medianBasis = showMedian && medianBasisOf(c);
  return (
    <div ref={rowRef} className={`relative ${medianBasis ? "h-[68px]" : "h-14"}`}>
      <span ref={floorRef} className="absolute left-0 top-1 flex w-min min-w-[56px] flex-col gap-[3px]">
        <span className="whitespace-nowrap bm-num-m text-bm-ink">{fmtMoneyShort(c.floor)}</span>
        <span className="bm-label-xs text-bm-muted">{c.floorIsP10 ? "Floor · 10th pct" : "Floor"}</span>
      </span>
      {showMedian && (
        <span
          ref={medianRef}
          aria-hidden={clash || undefined}
          className={`absolute top-1 flex ${medianBasis ? "w-[104px]" : "w-min min-w-[56px]"} -translate-x-1/2 flex-col items-center gap-[3px] text-center${clash ? " invisible" : ""}`}
          style={{ left: `${medianPos}%` }}
        >
          <span className="whitespace-nowrap bm-num-m text-bm-ink">{fmtMoneyShort(c.median)}</span>
          <MedianLabel c={c} className="bm-label-xs text-bm-muted" />
        </span>
      )}
      <span ref={ceilingRef} className="absolute right-0 top-1 flex w-min min-w-[56px] flex-col items-end gap-[3px] text-right">
        <span className="whitespace-nowrap bm-num-m text-bm-ink">{fmtMoneyShort(c.ceiling)}</span>
        <span className="bm-label-xs text-bm-muted">{c.ceilingIsP90 ? "Ceiling · 90th pct" : "Ceiling"}</span>
      </span>
    </div>
  );
};

// The athlete's amount and name over the bar (D5): centred on their mark,
// lined up with it near either end instead of hidden there (the 12% the
// card used to keep clear), on a row of their own so nothing else on the
// card can touch them. `amount` is in the card's format.
const YouMark = ({ amount, label, pos, compact }) => {
  const anchor = edgeAnchor(pos, 12);
  return (
    <div className={`relative ${compact ? "h-[38px]" : "h-[42px]"}`}>
      <span className={`absolute top-0 flex flex-col gap-[3px] ${ANCHOR_ALIGN[anchor]}`} style={anchoredStyle(pos, anchor)}>
        <span className="whitespace-nowrap bm-num-m text-bm-orange">{amount}</span>
        <span className="whitespace-nowrap bm-label-xs text-bm-orange">{label}</span>
      </span>
    </div>
  );
};

// The distribution's 10th or 90th percentile across the bar: a lighter
// tick than the median's, named in its tooltip.
const PercentileTick = ({ pos, title }) => (
  <span className="absolute top-[-3px] h-4 w-[2px] -translate-x-1/2 rounded-full bg-bm-tertiary" style={{ left: `${pos}%` }} title={title} />
);

// Type and boxes that change on the phone card; the desktop keeps its own.
const CONTEXT_STYLE = {
  desktop: {
    title: "bm-h-m",
    sub: "bm-body-m",
    upsideRow: "flex flex-col gap-3 py-4 sm:flex-row",
    upsideBox: "flex flex-1 flex-col items-center gap-[7px] rounded-[16px] border border-bm-border bg-white p-[22px] text-center",
    upsideValue: "bm-num-ml",
    rangeLine: "bm-body-m",
    frame: "rounded-[16px] border border-bm-border bg-white px-7 pb-7 pt-6",
    money: fmtMoney,
  },
  // Figma 2276:8797. The upside boxes stay side by side with short amounts;
  // their sides are 16px rather than 22px so they fit a 320px phone.
  compact: {
    title: "bm-h-s",
    sub: "bm-body-s",
    upsideRow: "flex gap-3 py-4",
    upsideBox: "flex min-w-0 flex-1 flex-col items-center gap-[7px] break-words rounded-[16px] border border-bm-border bg-white px-4 py-[22px] text-center",
    upsideValue: "bm-num-m",
    rangeLine: "bm-body-s",
    frame: "rounded-[16px] border border-bm-border bg-white px-[18px] py-4",
    money: fmtMoneyShort,
  },
};

// The same card renders inline on the page and inside the (i) modal.
// selfView (the athlete's own page, the default) speaks to the athlete; a
// viewer (coach, rep, brand, staff) reads it about the athlete, by first
// name when the profile has one. `compact` is the phone card (Figma
// 2276:8797): smaller type, short amounts in anchors that never collide
// (CompactAnchors), the upside boxes side by side and 10px compare pills.
export const ValueContextCard = ({ model, framed = false, selfView = true, compact = false }) => {
  const c = model.context;
  const first = model.firstName || "";
  const who = selfView ? "you" : first || "this athlete";
  const whose = selfView ? "your" : first ? `${first}'s` : "this athlete's";
  const path = selfView ? "your path" : "their path";
  if (!c) {
    return <p className="bm-body-m text-bm-muted">A valuation appears once {whose} profile has enough performance and audience data.</p>;
  }
  const span = c.ceiling - c.floor || 1;
  const pos = (v) => Math.max(0, Math.min(100, ((v - c.floor) / span) * 100));
  const youPos = pos(c.you);
  const medianPos = pos(c.median);
  const rangeLow = c.median;
  const rangeHigh = Math.round(c.you * 1.13);
  // The median's label keeps clear of the floor and ceiling at the ends;
  // the athlete's sits on its own row, so it never hides it (D5).
  const showMedian = medianPos > 12 && medianPos < 88;
  const youLabel = selfView ? "You" : first || "Athlete";
  const t = compact ? CONTEXT_STYLE.compact : CONTEXT_STYLE.desktop;
  // The distribution's 10th and 90th percentiles, ticked whenever there is
  // one, also where the bar reaches past them to the athlete's own range.
  const isAmount = (v) => typeof v === "number" && Number.isFinite(v);
  const bandTicks = [
    isAmount(c.p10) && { key: "p10", pos: pos(c.p10), title: `10th percentile · ${fmtMoney(c.p10)}` },
    isAmount(c.p90) && { key: "p90", pos: pos(c.p90), title: `90th percentile · ${fmtMoney(c.p90)}` },
  ].filter(Boolean);

  // Figma "Modal · Blank XL (960)": the same block renders inline on the page and in the (i) modal.
  const body = (
    <div className="flex flex-col gap-4">
      {framed && (
        <div className="flex flex-col gap-[6px]">
          <h2 className={`${t.title} text-bm-ink`}>{selfView ? "Your CacheValue™ in context" : "CacheValue™ in context"}</h2>
          <span className={`${t.sub} text-bm-muted`}>Where {fmtMoney(c.you)} puts {who}, and what it means for {path}</span>
        </div>
      )}
      {!framed && <span className={`${t.sub} text-bm-muted`}>Where {fmtMoney(c.you)} puts {who}, and what it means for {path}</span>}
      <div className="flex flex-col items-start gap-3 pb-4">
        {c.subline && <span className="bm-body-s text-bm-muted">{c.subline}</span>}
        {c.percentile && <ContextPercentile c={c} />}
      </div>
      <div className="flex flex-col gap-3">
        <YouMark amount={t.money(c.you)} label={youLabel} pos={youPos} compact={compact} />
        <div className="relative h-[10px] rounded-[20px] bg-bm-strong">
          <div className="absolute left-0 top-0 h-full rounded-[20px] bg-gradient-to-r from-bm-orange/30 to-bm-orange" style={{ width: `${youPos}%` }} />
          {bandTicks.map((tick) => (
            <PercentileTick key={tick.key} pos={tick.pos} title={tick.title} />
          ))}
          <span className="absolute top-[-3px] h-4 w-[2px] bg-bm-muted" style={{ left: `${medianPos}%` }} title={medianTitleOf(c)} />
          <span className="absolute top-1/2 h-[18px] w-[18px] -translate-x-1/2 -translate-y-1/2 rounded-full border-4 border-bm-orange bg-white shadow-[0px_1px_4px_rgba(0,0,0,0.2)]" style={{ left: `${youPos}%` }} />
        </div>
        {bandTicks.length > 0 && <span className="sr-only">{`${bandTicks.map((tick) => tick.title).join(", ")}.`}</span>}
        {compact ? (
          <CompactAnchors c={c} medianPos={medianPos} showMedian={showMedian} />
        ) : (
          // A median that names its group wraps the group in the phone's
          // 104px box rather than on one long line, which would reach the
          // floor or the ceiling. The row grows to the extra line.
          <div className={`relative ${showMedian && medianBasisOf(c) ? "h-[68px]" : "h-14"}`}>
            <span className="absolute left-0 top-1 flex flex-col gap-[3px]">
              <span className="bm-num-m text-bm-ink">{fmtMoney(c.floor)}</span>
              <span className="bm-label-xs text-bm-muted">{c.floorIsP10 ? "Floor · 10th pct" : "Floor"}</span>
            </span>
            {showMedian && (
              <span
                className={`absolute top-1 flex${medianBasisOf(c) ? " w-[104px]" : ""} -translate-x-1/2 flex-col items-center gap-[3px] text-center`}
                style={{ left: `${medianPos}%` }}
              >
                <span className="bm-num-m text-bm-ink">{fmtMoney(c.median)}</span>
                <MedianLabel c={c} className="bm-label-xs text-bm-muted" />
              </span>
            )}
            <span className="absolute right-0 top-1 flex flex-col items-end gap-[3px] text-right">
              <span className="bm-num-m text-bm-ink">{fmtMoney(c.ceiling)}</span>
              <span className="bm-label-xs text-bm-muted">{c.ceilingIsP90 ? "Ceiling · 90th pct" : "Ceiling"}</span>
            </span>
          </div>
        )}
      </div>
      <div className={t.upsideRow}>
        <div className={t.upsideBox}>
          <span className={`${t.upsideValue} text-bm-ink`}>{t.money(c.you)} → {t.money(c.ceiling)}</span>
          <span className="bm-body-s text-bm-muted">Upside if Visibility pillar fully unlocked</span>
        </div>
        <div className={t.upsideBox}>
          <span className={`${t.upsideValue} text-bm-ink`}>+{t.money(c.gapToCeiling)}</span>
          <span className="bm-body-s text-bm-muted">Estimated gap to ceiling</span>
        </div>
      </div>
      <div className="h-px w-full bg-bm-border" />
      <p className={`pt-4 ${t.rangeLine} text-bm-ink`}>Athletes valued in {whose} range ({fmtMoney(rangeLow)}-{fmtMoney(rangeHigh)}) typically compete at:</p>
      <div className="flex flex-wrap gap-[10px] py-1">
        {(c.comparePills || []).map((pill) =>
          compact ? (
            <span key={pill.bold} className="inline-block max-w-full rounded-full border border-bm-strong bg-white px-4 py-[9px] bm-label-xs">
              <span className="font-bold text-bm-ink">{pill.bold}</span>
              <span className="text-bm-muted">&nbsp;· {pill.rest}</span>
            </span>
          ) : (
            <span key={pill.bold} className="inline-flex h-9 items-center rounded-full border border-bm-strong bg-white px-4 text-[13px] leading-none">
              <span className="font-bold text-bm-ink">{pill.bold}</span>
              <span className="text-bm-muted">&nbsp;· {pill.rest}</span>
            </span>
          ),
        )}
      </div>
    </div>
  );
  if (!framed) return body;
  return <div className={t.frame}>{body}</div>;
};

export const ValueContextModal = ({ model, onClose, selfView = true }) => (
  <BmModal title="CacheValue™ in context" onClose={onClose} width={960}>
    <ValueContextCard model={model} selfView={selfView} />
  </BmModal>
);
